/**
 * Quotations Service
 * API client for Purchase and Sales Quotations management
 */

import { get, post, put, del, buildFilterParams } from './apiClient';
import type {
  PurchaseQuotation,
  SalesQuotation,
  Quotation,
  PurchaseQuotationFilter,
  SalesQuotationFilter,
  PurchaseQuotationListResponse,
  SalesQuotationListResponse,
  CreatePurchaseQuotationRequest,
  CreateSalesQuotationRequest,
  UpdatePurchaseQuotationRequest,
  UpdateSalesQuotationRequest,
} from '@/types/quotations';

const BASE_URL = '/api/v1/psi/quotations';

// ==================== Purchase Quotations ====================

/**
 * List purchase quotations with optional filters
 */
export async function listPurchaseQuotations(
  filter?: PurchaseQuotationFilter
): Promise<PurchaseQuotationListResponse> {
  const params = buildFilterParams(filter);
  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}/purchase?${queryString}` : `${BASE_URL}/purchase`;
  return get<PurchaseQuotationListResponse>(url);
}

/**
 * Get a single purchase quotation by ID
 */
export async function getPurchaseQuotation(id: string): Promise<PurchaseQuotation> {
  return get<PurchaseQuotation>(`${BASE_URL}/purchase/${id}`);
}

/**
 * Create a new purchase quotation
 */
export async function createPurchaseQuotation(
  data: CreatePurchaseQuotationRequest
): Promise<PurchaseQuotation> {
  return post<PurchaseQuotation>(`${BASE_URL}/purchase`, data);
}

/**
 * Update a purchase quotation
 */
export async function updatePurchaseQuotation(
  id: string,
  data: UpdatePurchaseQuotationRequest
): Promise<PurchaseQuotation> {
  return put<PurchaseQuotation>(`${BASE_URL}/purchase/${id}`, data);
}

/**
 * Delete a purchase quotation
 */
export async function deletePurchaseQuotation(id: string): Promise<void> {
  return del(`${BASE_URL}/purchase/${id}`);
}

/**
 * Get purchase quotations by vendor code
 */
export async function getPurchaseQuotationsByVendor(vendorCode: string): Promise<PurchaseQuotation[]> {
  return get<PurchaseQuotation[]>(`${BASE_URL}/purchase/by-vendor/${vendorCode}`);
}

// ==================== Sales Quotations ====================

/**
 * List sales quotations with optional filters
 */
export async function listSalesQuotations(
  filter?: SalesQuotationFilter
): Promise<SalesQuotationListResponse> {
  const params = buildFilterParams(filter);
  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}/sales?${queryString}` : `${BASE_URL}/sales`;
  return get<SalesQuotationListResponse>(url);
}

/**
 * Get a single sales quotation by ID
 */
export async function getSalesQuotation(id: string): Promise<SalesQuotation> {
  return get<SalesQuotation>(`${BASE_URL}/sales/${id}`);
}

/**
 * Create a new sales quotation
 */
export async function createSalesQuotation(
  data: CreateSalesQuotationRequest
): Promise<SalesQuotation> {
  return post<SalesQuotation>(`${BASE_URL}/sales`, data);
}

/**
 * Update a sales quotation
 */
export async function updateSalesQuotation(
  id: string,
  data: UpdateSalesQuotationRequest
): Promise<SalesQuotation> {
  return put<SalesQuotation>(`${BASE_URL}/sales/${id}`, data);
}

/**
 * Delete a sales quotation
 */
export async function deleteSalesQuotation(id: string): Promise<void> {
  return del(`${BASE_URL}/sales/${id}`);
}

/**
 * Get sales quotations by customer code
 */
export async function getSalesQuotationsByCustomer(customerCode: string): Promise<SalesQuotation[]> {
  return get<SalesQuotation[]>(`${BASE_URL}/sales/by-customer/${customerCode}`);
}

// ==================== Cross-type Queries ====================

/**
 * Get quotations by part code (across both types)
 */
export async function getQuotationsByPartCode(partCode: string): Promise<Quotation[]> {
  return get<Quotation[]>(`${BASE_URL}/by-part/${partCode}`);
}

// ==================== Firestore-based Purchase Quotation Queries ====================
// These use the new Firestore endpoints for efficient vendor/part queries

const FIRESTORE_PQ_URL = '/api/v1/psi/purchase-quotations';

/**
 * QuotationItemIndex represents a vendor+part combination with pricing info
 */
export interface QuotationItemIndex {
  id: string;
  vendor_code: string;
  part_code: string;
  vendor_name: string;
  description: string;
  specification: string;
  best_price: number;
  best_quotation_id: string;
  best_lead_time_days: number;
  has_valid_quotation: boolean;
  quotation_refs?: QuotationRef[];
}

export interface QuotationRef {
  quotation_id: string;
  valid_from: string;
  valid_until: string;
  pricing_tiers: PricingTier[];
  currency: string;
  lead_time_days: number;
}

export interface PricingTier {
  min_qty: number;
  max_qty?: number | null;
  unit_price: number;
}

export interface PartsByVendorResponse {
  items: QuotationItemIndex[];
  total: number;
}

export interface VendorsByPartResponse {
  items: QuotationItemIndex[];
  total: number;
}

export interface BestPriceResponse {
  found: boolean;
  best_price?: number;
  best_quotation_id?: string;
  lead_time_days?: number;
  vendor_code?: string;
  part_code?: string;
  description?: string;
  specification?: string;
}

/**
 * Get all parts quoted by a specific vendor
 * This is the key API for PO part selection
 */
export async function getPartsByVendor(vendorCode: string): Promise<PartsByVendorResponse> {
  return get<PartsByVendorResponse>(`${FIRESTORE_PQ_URL}/parts-by-vendor?vendor_code=${encodeURIComponent(vendorCode)}`);
}

/**
 * Get all vendors that have quoted a specific part
 */
export async function getVendorsByPart(partCode: string): Promise<VendorsByPartResponse> {
  return get<VendorsByPartResponse>(`${FIRESTORE_PQ_URL}/vendors-by-part?part_code=${encodeURIComponent(partCode)}`);
}

/**
 * Get the best price for a specific vendor+part combination
 */
export async function getBestPrice(vendorCode: string, partCode: string): Promise<BestPriceResponse> {
  return get<BestPriceResponse>(`${FIRESTORE_PQ_URL}/best-price?vendor_code=${encodeURIComponent(vendorCode)}&part_code=${encodeURIComponent(partCode)}`);
}

/**
 * Trigger sync from Google Sheets to Firestore (admin only)
 */
export async function syncFromSheets(): Promise<{ status: string }> {
  return post<{ status: string }>(`${FIRESTORE_PQ_URL}/sync-from-sheets`, {});
}
